import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";
import { whatsappLink } from "../lib/clinic";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "frequency",
    question: "How often do you grind or clench your teeth?",
    options: [
      "Almost every night",
      "A few times a week",
      "Occasionally — mostly when stressed",
      "I don't think I do",
    ],
  },
  {
    id: "wakeup",
    question: "How does your jaw feel when you wake up?",
    options: [
      "Sore, tight, or aching",
      "Stiff but eases off quickly",
      "Tender to touch on the cheeks",
      "Generally fine",
    ],
  },
  {
    id: "muscle_tightness",
    question: "Do you notice tightness or pain in your jaw, cheeks or neck?",
    options: [
      "Yes — tight, knotted muscles most days",
      "Often around the cheeks or temples",
      "Neck and shoulder tension too",
      "Not really",
    ],
  },
  {
    id: "mouth_opening",
    question: "Can you open your mouth fully and comfortably?",
    options: [
      "No — it feels restricted or limited",
      "It clicks, locks or catches sometimes",
      "Opens fully but feels tight or sore",
      "Yes — no issues opening",
    ],
  },
  {
    id: "headaches",
    question: "Do you get tension headaches?",
    options: [
      "Yes — most mornings",
      "A few times a week",
      "Occasionally",
      "Rarely or never",
    ],
  },
  {
    id: "tooth_wear",
    question: "Have you noticed worn, chipped or sensitive teeth?",
    options: [
      "Yes — clearly worn or chipped",
      "Increased sensitivity recently",
      "My dentist has flagged wear",
      "Not that I've noticed",
    ],
  },
  {
    id: "partner",
    question: "Has anyone heard you grinding in your sleep?",
    options: [
      "Yes — partner mentions it often",
      "Once or twice",
      "I sleep alone, unsure",
      "No",
    ],
  },
  {
    id: "stress",
    question: "How would you describe your current stress levels?",
    options: ["Very high", "Moderate", "Mild", "Low"],
  },
  {
    id: "tried",
    question: "What have you tried so far?",
    options: [
      "Nothing yet",
      "A night guard (mouth splint)",
      "Massage / physio / yoga",
      "Multiple things — nothing has stuck",
    ],
  },
];

export const AIQuiz = () => {
  const [step, setStep] = useState(0); // 0..QUESTIONS.length, then 'result'
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const total = QUESTIONS.length;
  const isOnQuestion = step >= 0 && step < total;
  const isOnContact = step === total;
  const isOnResult = step === total + 1;

  const progressPct = isOnResult
    ? 100
    : Math.round(((step + 1) / (total + 1)) * 100);

  const handleSelect = (qid, answer) => {
    setAnswers((a) => ({ ...a, [qid]: answer }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const validateContact = () => {
    if (!contact.name.trim()) return "Please add your name.";
    if (!contact.phone.trim() || contact.phone.replace(/\D/g, "").length < 7)
      return "Please add a valid mobile number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email.trim()))
      return "Please add a valid email address.";
    return null;
  };

  const handleSubmit = async () => {
    const v = validateContact();
    if (v) { setError(v); return; }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: contact.name.trim(),
        phone: contact.phone.trim(),
        email: contact.email.trim(),
        answers: QUESTIONS.map((q) => ({
          question: q.question,
          answer: answers[q.id] || "Not answered",
        })),
      };
      const { data } = await axios.post(`${API}/quiz/assess`, payload);
      setResult(data);
      setStep(total + 1);
    } catch (e) {
      console.error(e);
      setError(
        "We couldn't run the AI assessment right now. Please WhatsApp us and we'll review your answers personally.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setContact({ name: "", phone: "", email: "" });
    setResult(null);
    setError(null);
  };

  const verdictColor = (score) => {
    if (score >= 75) return "text-[#4ADE80]";
    if (score >= 50) return "text-[#93C5FD]";
    return "text-white/70";
  };

  return (
    <section
      id="quiz"
      data-testid="quiz-section"
      className="py-24 lg:py-32 bg-rb-bg"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="font-overline mb-4 inline-flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} /> AI candidate check
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-rb-text leading-[1.1]">
            Are you a candidate?
          </h2>
          <p className="mt-4 text-rb-text-2 max-w-xl mx-auto">
            Nine quick questions. A personalised, AI-powered assessment in
            seconds — and Dr. Patel keeps your details on file so you can pick
            up where you left off if you decide to book in.
          </p>
        </div>

        <div className="card-soft p-6 sm:p-10">
          {/* Progress bar */}
          {!isOnResult && (
            <div className="mb-8">
              <div className="flex justify-between text-xs text-rb-text-2 mb-2">
                <span data-testid="quiz-progress-label">
                  {isOnContact
                    ? "Almost done"
                    : `Question ${step + 1} of ${total}`}
                </span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.4 }}
                  className="h-full bg-[#93C5FD]"
                />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {isOnQuestion && (
              <motion.div
                key={`q-${step}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
                data-testid={`quiz-question-${step}`}
              >
                <h3 className="font-serif-display text-2xl sm:text-3xl text-rb-text mb-6">
                  {QUESTIONS[step].question}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {QUESTIONS[step].options.map((opt) => {
                    const selected = answers[QUESTIONS[step].id] === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => handleSelect(QUESTIONS[step].id, opt)}
                        data-testid={`quiz-option-${step}-${opt
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")
                          .slice(0, 30)}`}
                        className={`text-left p-4 rounded-xl border transition-all ${
                          selected
                            ? "border-[#93C5FD] bg-white/10"
                            : "border-white/10 bg-white/[0.03] hover:border-[#93C5FD]/50 hover:bg-white/5"
                        }`}
                      >
                        <span className="text-sm text-rb-text">{opt}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-8 flex justify-between">
                  <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    data-testid="quiz-prev-btn"
                    className="text-sm text-rb-text-2 hover:text-rb-text disabled:opacity-30 inline-flex items-center gap-1"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!answers[QUESTIONS[step].id]}
                    data-testid="quiz-next-btn"
                    className="text-sm text-rb-text-2 hover:text-rb-text disabled:opacity-30 inline-flex items-center gap-1"
                  >
                    Skip <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            )}

            {isOnContact && (
              <motion.div
                key="contact"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
                data-testid="quiz-contact-step"
              >
                <h3 className="font-serif-display text-2xl sm:text-3xl text-rb-text mb-3">
                  One last step — where shall we send your assessment?
                </h3>
                <p className="text-sm text-rb-text-2 mb-6">
                  Dr. Patel will keep your details on file so when you're ready
                  to book in, we can pick up exactly where you left off.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <input
                    value={contact.name}
                    onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                    placeholder="Your name"
                    data-testid="quiz-name-input"
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04] focus:outline-none focus:border-[#93C5FD] text-rb-text placeholder:text-white/40"
                  />
                  <input
                    value={contact.phone}
                    onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                    placeholder="Mobile number"
                    type="tel"
                    data-testid="quiz-phone-input"
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04] focus:outline-none focus:border-[#93C5FD] text-rb-text placeholder:text-white/40"
                  />
                </div>
                <input
                  value={contact.email}
                  onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                  placeholder="Email address"
                  type="email"
                  data-testid="quiz-email-input"
                  className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04] focus:outline-none focus:border-[#93C5FD] text-rb-text placeholder:text-white/40"
                />
                {error && (
                  <p className="text-sm text-[#FCA5A5] mt-4" data-testid="quiz-error">
                    {error}
                  </p>
                )}
                <p className="text-xs text-rb-text-2 mt-4">
                  We never share your details. You'll get your assessment instantly on this screen — no spam, no email lists.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid="quiz-submit-btn"
                    className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />{" "}
                        Analysing your answers...
                      </>
                    ) : (
                      <>
                        Get my assessment{" "}
                        <ArrowRight size={16} strokeWidth={1.75} />
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="quiz-back-btn"
                    className="text-sm text-rb-text-2 hover:text-rb-text inline-flex items-center gap-1"
                  >
                    <ArrowLeft size={14} /> Review answers
                  </button>
                </div>
              </motion.div>
            )}

            {isOnResult && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                data-testid="quiz-result"
              >
                <div className="font-overline mb-4">Your assessment</div>
                <div className="flex items-baseline gap-3 mb-2">
                  <span
                    className={`font-serif-display text-6xl ${verdictColor(
                      result.score,
                    )}`}
                    data-testid="quiz-score"
                  >
                    {result.score}
                  </span>
                  <span className="text-sm text-rb-text-2">/ 100</span>
                </div>
                <h3
                  className="font-serif-display text-2xl sm:text-3xl text-rb-text mb-4"
                  data-testid="quiz-verdict"
                >
                  {result.verdict}
                </h3>
                <p
                  className="text-rb-text-2 leading-relaxed mb-6"
                  data-testid="quiz-assessment"
                >
                  {result.assessment}
                </p>
                <div className="bg-white/[0.04] rounded-xl p-5 border border-white/10 mb-8">
                  <div className="font-overline mb-2">Recommended next step</div>
                  <p className="text-rb-text" data-testid="quiz-recommendation">
                    {result.recommendation}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={whatsappLink(
                      `Hi, this is ${contact.name}. My Clenchless quiz scored ${result.score}/100 (${result.verdict}). I'd like to make an enquiry.`,
                    )}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="quiz-cta-whatsapp"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                  >
                    Send my result to Dr. Patel on WhatsApp{" "}
                    <ArrowRight size={16} strokeWidth={1.75} />
                  </a>
                  <button
                    onClick={reset}
                    data-testid="quiz-reset-btn"
                    className="text-sm text-rb-text-2 hover:text-rb-text inline-flex items-center gap-2 px-4"
                  >
                    <RotateCcw size={14} /> Retake
                  </button>
                </div>
                <p className="text-xs text-rb-text-2 mt-6">
                  This AI assessment is informational only and does not replace
                  a clinical examination.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
